import { createComponentStore } from "../../utils/createComponentStore";
import {
  ensureCreationOutputPlaceholder,
  newCreationOutputPlaceholder,
} from "../../utils/creationOutputPlaceholder";
import {
  DEFAULT_ARTIFACT_ALLOWED_EVENTS,
  DEFAULT_ARTIFACT_TYPE,
  getDefaultArtifactAllowedEvents,
  getArtifactTypePresentation,
} from "../pages/artifacts/artifactDefaults";

const emptyData = () => ({
  title: "",
  artifactType: DEFAULT_ARTIFACT_TYPE,
  sourceLanguage: getArtifactTypePresentation(DEFAULT_ARTIFACT_TYPE)
    .defaultSourceLanguage,
  renderer: "safe_html",
  description: "",
  mode: "INLINE",
  artifactDefinitionId: null,
  artifactDefinitionVersion: null,
  html: getArtifactTypePresentation(DEFAULT_ARTIFACT_TYPE).defaultSource,
  allowedEvents: DEFAULT_ARTIFACT_ALLOWED_EVENTS,
  accessPolicy: { mode: "PUBLIC", selectedUserIds: [] },
  sharing: {},
  taskConfig: null,
  singleUse: true,
  expiresInHours: null,
  creationOutputPlaceholder: newCreationOutputPlaceholder("artifact"),
  submissionOutputPlaceholder: {
    key: "artifactEvent",
    systemOutput: "artifactEvent",
    type: "json",
  },
});

const normalizeEvents = (events) =>
  Array.isArray(events) && events.length > 0
    ? events
    : DEFAULT_ARTIFACT_ALLOWED_EVENTS;

const { getStore: getArtifactStore, defaultStore } = createComponentStore({
  type: "artifact",
  endpoint: "artifact",
  registryKey: "artifact",
  initData: emptyData,
  onLoadData: (rawData) => ({
    ...emptyData(),
    ...(rawData || {}),
    allowedEvents: normalizeEvents(rawData?.allowedEvents),
    creationOutputPlaceholder: ensureCreationOutputPlaceholder(
      "artifact",
      rawData?.creationOutputPlaceholder
    ),
    submissionOutputPlaceholder: rawData?.submissionOutputPlaceholder || {
      key: "artifactEvent",
      systemOutput: "artifactEvent",
      type: "json",
    },
  }),
  extraActions: (set, get) => ({
    setTitle: (title) => set({ data: { ...get().data, title } }),
    setDescription: (description) =>
      set({ data: { ...get().data, description } }),
    setMode: (mode) => {
      const current = get().data;
      set({
        data: {
          ...current,
          mode,
          ...(mode === "INLINE"
            ? {
                artifactDefinitionId: null,
                artifactDefinitionVersion: null,
              }
            : {}),
        },
      });
    },
    setArtifactType: (artifactType) => {
      const typePresentation = getArtifactTypePresentation(artifactType);
      set({
        data: {
          ...get().data,
          artifactType,
          renderer: typePresentation.renderer,
          sourceLanguage: typePresentation.defaultSourceLanguage,
          html: typePresentation.defaultSource,
          allowedEvents: getDefaultArtifactAllowedEvents(artifactType),
        },
      });
    },
    setSourceLanguage: (sourceLanguage) =>
      set({ data: { ...get().data, sourceLanguage } }),
    setDefinition: ({ artifactDefinitionId, artifactDefinitionVersion }) =>
      set({
        data: {
          ...get().data,
          artifactDefinitionId,
          artifactDefinitionVersion,
        },
      }),
    setHtml: (html) => set({ data: { ...get().data, html } }),
    setAllowedEvents: (allowedEvents) =>
      set({ data: { ...get().data, allowedEvents } }),
    setAccessPolicy: (accessPolicy) =>
      set({ data: { ...get().data, accessPolicy } }),
    setSingleUse: (singleUse) => set({ data: { ...get().data, singleUse } }),
    setCreationOutputPlaceholderKey: (key) =>
      set({
        data: {
          ...get().data,
          creationOutputPlaceholder: {
            ...(get().data.creationOutputPlaceholder || {}),
            key,
          },
        },
      }),
    setSubmissionOutputPlaceholderKey: (key) =>
      set({
        data: {
          ...get().data,
          submissionOutputPlaceholder: {
            ...(get().data.submissionOutputPlaceholder || {}),
            key,
          },
        },
      }),
  }),
});

export { getArtifactStore };
export default defaultStore;
